import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { organisationsAPI } from '../../../../organisations/api/organisationsAPI';
import { isForbidden } from '../../../../organisations/lib/errorMessage';
import { ExhibitLightboxProvider, useMathJaxTypeset } from '../../../../quiz-generation/components/examRender';
import EmptyState from '../../common/EmptyState';
import { dayMonthYear, modeLabel, paperKindLabel } from './lib/activityFormat';
import {
  gridTone, markedCount, needsLook, needsLookCount, paperRefFor, parsePaperRef, partCaption, reviewScore,
  sittingNote, withPassages,
} from './lib/paperReview';
import PaperReviewQuestion, { PassageBlock } from './PaperReviewQuestion';

const fetchPart = (studentId, ref) => {
  const parsed = parsePaperRef(ref);
  if (!parsed) return Promise.reject(Object.assign(new Error('No paper'), { status: 404 }));
  return parsed.source === 'app_session'
    ? organisationsAPI.getStudentAppPaperReview(studentId, parsed.id)
    : organisationsAPI.getStudentPaperReview(studentId, parsed.id);
};

const failure = (err) => {
  if (err?.status === 404) return { status: 'not_found' };
  if (isForbidden(err)) return { status: 'forbidden' };
  return { status: 'error' };
};

/**
 * Every part this review has asked for, by ref: { status, data }. The open
 * part loads first; once it is ready, its sitting's other parts load for
 * their tabs' scores. A part is fetched once per review.
 */
function useReviewParts(studentId, paperRef) {
  const [parts, setParts] = useState({});
  const requested = useRef(new Set());
  const [attempt, setAttempt] = useState(0);

  const load = useCallback((ref) => {
    if (!ref || requested.current.has(ref)) return;
    requested.current.add(ref);
    setParts((prev) => ({ ...prev, [ref]: { status: 'loading' } }));
    fetchPart(studentId, ref)
      .then((data) => setParts((prev) => ({ ...prev, [ref]: { status: 'ready', data } })))
      .catch((err) => setParts((prev) => ({ ...prev, [ref]: failure(err) })));
  }, [studentId]);

  useEffect(() => { load(paperRef); }, [load, paperRef, attempt]);

  const current = parts[paperRef] || { status: 'loading' };
  const siblings = current.status === 'ready' ? current.data.attempt.sibling_parts || [] : [];
  const siblingKey = siblings.map(paperRefFor).join('|');
  useEffect(() => {
    siblingKey.split('|').filter(Boolean).forEach(load);
  }, [siblingKey, load]);

  const retry = useCallback(() => {
    requested.current.delete(paperRef);
    setAttempt((n) => n + 1);
  }, [paperRef]);

  return { current, parts, retry };
}

const ReviewHeader = ({ attempt }) => {
  const kind = paperKindLabel(attempt.kind);
  const mode = modeLabel(attempt.mode);
  const note = sittingNote(attempt);
  const sub = [
    dayMonthYear(attempt.completed_at || attempt.started_at),
    mode ? mode.toLowerCase() : null,
    attempt.status === 'in_progress' ? 'not finished' : null,
    note?.recorded ? note.text : null,
  ].filter(Boolean).join(' · ');
  return (
    <div className="td-pr-top">
      <span className="td-eyebrow">{[attempt.exam, kind].filter(Boolean).join(' · ')}</span>
      <h2 className="td-pr-title">{attempt.paper_name}</h2>
      {sub ? <p className="td-act-meta">{sub}</p> : null}
      {note && !note.recorded ? <p className="td-act-note td-pr-notice">{note.text}</p> : null}
    </div>
  );
};

/** One tab per part of the sitting, each with its score and time once loaded. */
const PartTabs = ({ attempt, parts, currentRef, onOpenPaper }) => {
  const siblings = attempt.sibling_parts || [];
  if (siblings.length < 2) return null;
  return (
    <div className="td-pr-parts" role="group" aria-label="Paper part">
      {siblings.map((part, i) => {
        const ref = paperRefFor(part);
        const loaded = parts[ref];
        return (
          <button
            key={ref}
            type="button"
            aria-pressed={ref === currentRef}
            onClick={() => { if (ref !== currentRef) onOpenPaper(ref, { replace: true }); }}
          >
            <b>{part.part_label || `Part ${i + 1}`}</b>
            <small>{loaded?.status === 'ready' ? partCaption(loaded.data) : ' '}</small>
          </button>
        );
      })}
    </div>
  );
};

/**
 * Correct, wrong and not answered, then the weakest topic when the review
 * has one to name (a custom quiz's summary has no weakest_topic).
 */
export const SummaryTiles = ({ summary }) => (
  <div className="td-act-statrow td-pr-stats">
    <div className="td-stat">
      <span className="td-stat__eyebrow">Correct</span>
      <span className="td-stat__value">{`${summary.correct} / ${markedCount(summary)}`}</span>
      <span className="td-stat__caption">{reviewScore(summary).detail}</span>
    </div>
    <div className="td-stat">
      <span className="td-stat__eyebrow">Wrong</span>
      <span className="td-stat__value">{summary.wrong}</span>
    </div>
    <div className="td-stat">
      <span className="td-stat__eyebrow">Not answered</span>
      <span className="td-stat__value">{summary.unanswered}</span>
    </div>
    {'weakest_topic' in summary ? (
      <div className="td-stat">
        <span className="td-stat__eyebrow">Weakest topic</span>
        <span className="td-stat__value td-pr-stats__topic">{summary.weakest_topic || '—'}</span>
      </div>
    ) : null}
  </div>
);

const GRID_WORDS = {
  r: 'correct', w: 'wrong', u: 'not answered', n: 'not marked',
};

/** Every question as a square: green correct, red wrong, dashed not answered. A click jumps to it. */
const QuestionGrid = ({ questions, onJump }) => (
  <nav className="td-pr-grid" aria-label="Questions: green correct, red wrong, dashed not answered">
    {questions.map((q) => {
      const tone = gridTone(q.outcome);
      return (
        <button
          key={q.number}
          type="button"
          className={`td-pr-grid__q td-pr-grid__q--${tone}`}
          aria-label={`Question ${q.number}, ${q.outcome === 'not_recorded' ? 'answer not recorded' : GRID_WORDS[tone]}`}
          onClick={() => onJump(q.number)}
        >
          {q.number}
        </button>
      );
    })}
  </nav>
);

const PAPER_FOOTNOTE = 'Questions are drawn as the student saw them. Time per question shows only where it was recorded.';

/**
 * The open part: notes, tiles, grid, filter and question cards. Also the
 * custom quiz review's body (QuizReview, through lib/quizReview's adapter),
 * with its own `footnote`.
 */
export const PartBody = ({ review, onOpenPiece, footnote = PAPER_FOOTNOTE }) => {
  const { summary, questions } = review;
  const [filter, setFilter] = useState('all');
  const [jumpTo, setJumpTo] = useState(null);
  const listRef = useRef(null);
  const cards = useRef({});

  const shown = filter === 'all' ? questions : questions.filter(needsLook);
  useMathJaxTypeset(listRef, [review, filter]);

  useEffect(() => {
    if (jumpTo == null) return;
    const card = cards.current[jumpTo];
    if (card) {
      if (typeof card.scrollIntoView === 'function') card.scrollIntoView({ block: 'start' });
      card.focus();
    }
    setJumpTo(null);
  }, [jumpTo]);

  const jump = (number) => {
    const target = questions.find((q) => q.number === number);
    if (filter !== 'all' && target && !needsLook(target)) setFilter('all');
    setJumpTo(number);
  };

  const marked = markedCount(summary);
  const essays = questions.filter((q) => q.kind === 'essay_choice_group').length;
  return (
    <>
      {summary.not_recorded > 0 ? (
        <p className="td-act-note td-pr-notice">
          {`pathbuddy.app did not record which answer the student chose for ${summary.not_recorded === questions.length ? 'these questions' : `${summary.not_recorded} of these questions`}. The questions and their correct answers are shown.`}
        </p>
      ) : null}
      {marked > 0 ? <SummaryTiles summary={summary} /> : null}
      {marked === 0 && essays > 0 ? (
        <p className="td-act-note">Essays are not marked right or wrong. The student’s essay is below.</p>
      ) : null}

      {questions.length > 1 ? <QuestionGrid questions={questions} onJump={jump} /> : null}
      {marked > 0 ? (
        <div className="td-act-toggle td-pr-filter" role="group" aria-label="Show questions">
          <button type="button" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
            {`All ${questions.length}`}
          </button>
          <button type="button" aria-pressed={filter === 'look'} onClick={() => setFilter('look')}>
            {`Wrong or not answered · ${needsLookCount(summary)}`}
          </button>
        </div>
      ) : null}

      <div ref={listRef}>
        {shown.length ? withPassages(shown).map(({ question, passage }) => (
          <React.Fragment key={question.number}>
            {passage ? <PassageBlock exhibit={passage} /> : null}
            <PaperReviewQuestion
              question={question}
              onOpenPiece={onOpenPiece}
              cardRef={(el) => { cards.current[question.number] = el; }}
            />
          </React.Fragment>
        )) : (
          <p className="td-act-meta">
            {questions.length ? 'No questions were wrong or left unanswered.' : 'This paper has no questions.'}
          </p>
        )}
      </div>
      {footnote ? <p className="td-act-note">{footnote}</p> : null}
    </>
  );
};

/**
 * A paper attempt question by question, for staff (mockup state 4). Opened
 * from an Exam practice row with ?paper=<ref> (lib/paperReview); a sitting's
 * parts are tabs, each loaded by its own id. `onOpenPaper(ref, { replace:
 * true })` switches part; `onBack` returns to the Activity tab; `onOpenPiece` opens an
 * essay's Writing piece.
 */
const PaperReview = ({
  studentId, studentName, paperRef, onOpenPaper, onBack, onOpenPiece,
}) => {
  const { current, parts, retry } = useReviewParts(studentId, paperRef);

  let body = null;
  if (current.status === 'loading') {
    body = <p className="td-loading" role="status">Loading paper…</p>;
  } else if (current.status === 'not_found') {
    body = <EmptyState title="This paper could not be found" body="It may have been removed, or it is not this student’s." />;
  } else if (current.status === 'forbidden') {
    body = <EmptyState tone="warning" title="You do not have access to this student." />;
  } else if (current.status === 'error') {
    body = (
      <EmptyState
        tone="warning"
        title="Could not load this paper"
        action={<button type="button" className="td-btn td-btn--small td-act-retry" onClick={retry}>Retry</button>}
      />
    );
  } else {
    const review = current.data;
    body = (
      <>
        <ReviewHeader attempt={review.attempt} />
        <PartTabs attempt={review.attempt} parts={parts} currentRef={paperRef} onOpenPaper={onOpenPaper} />
        <PartBody key={paperRef} review={review} onOpenPiece={onOpenPiece} />
      </>
    );
  }

  return (
    <ExhibitLightboxProvider>
      <section className="td-act td-pr" aria-label="Paper review">
        <button type="button" className="td-btn td-btn--ghost td-profile__back" onClick={onBack}>
          {`← ${studentName || 'Student'} · Activity`}
        </button>
        {body}
      </section>
    </ExhibitLightboxProvider>
  );
};

export default PaperReview;
